<template>
  <div class="mx-sm-1 mt-9">
    <div class="page-header">
      <h1 class="page-title fw-bold">Détail de la vente</h1>
    </div>
    <div class="mb-3">
      <button class="btn btn-outline-dark btn-sm" @click="goBack">
        <i class="fe fe-arrow-left me-1"></i> Retour à la liste
      </button>
    </div>

    <div class="row">
      <!-- Colonne principale -->
      <div class="col-xl-8 col-md-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between">
            <h3 class="card-title">Informations client & vente</h3>
            <button class="btn btn-outline-secondary btn-sm" @click="showHistory">
              <i class="fe fe-clock"></i> Historique
            </button>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-bordered">
                <tr>
                  <td class="fw-bold">Référence</td>
                  <td>{{ sale.reference }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">Date</td>
                  <td>{{ sale.saleDate }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">Vendeur</td>
                  <td>{{ sale.sellerName }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">Client</td>
                  <td>{{ sale.clientName || 'N/A' }}</td>
                </tr>
                <tr>
                  <td class="fw-bold">Statut</td>
                  <td>
        <span class="badge"
              :class="{
                'bg-success': sale.status === 'VALIDATED',
                'bg-warning': sale.status === 'PENDING',
                'bg-danger': sale.status === 'REJECTED'
              }">
          {{ sale.status }}
        </span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div class="card-footer text-end" v-if="sale.status === 'PENDING'">
            <button class="btn btn-success me-2" @click="validateSale">
              <i class="fe fe-check-circle me-1"></i> Valider
            </button>
            <button class="btn btn-danger" @click="rejectSale">
              <i class="fe fe-x-circle me-1"></i> Rejeter
            </button>
          </div>
        </div>

        <!-- Liste des articles -->
        <div class="card mt-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h3 class="card-title">Articles vendus</h3>
            <span v-if="sale.status !== 'PENDING'" class="badge bg-success" v-text="'Quantités validées'"></span>
          </div>

          <div class="card-body table-responsive" v-if="sale.status === 'PENDING'">
            <table class="table table-bordered">
              <thead>
              <tr>
                <th>Produit</th>
                <th>PU</th>
                <th>Demandée</th>
                <th>Validée</th>
                <th>Sous-total</th>
                <th>Action</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="line in editableLines" :key="line.id">
                <td>{{ line.productCode }}</td>
                <td>{{ line.unitPrice }} FCFA</td>
                <td>{{ line.requestedQuantity }}</td>
                <td>
                  <div class="input-group input-group-sm">
                    <button
                        class="btn btn-outline-secondary"
                        @click="decrement(line)"
                        :disabled="line.validatedQuantity <= 1"
                    >-</button>
                    <input type="number" v-model.number="line.validatedQuantity" class="form-control text-center" min="0">
                    <button class="btn btn-outline-secondary" @click="increment(line)">+</button>
                  </div>
                </td>
                <td>{{ (line.unitPrice * line.validatedQuantity) }} FCFA</td>
                <td>
                  <button class="btn btn-sm btn-danger" @click="removeLine(line.id)">
                    <i class="fe fe-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="editableLines.length === 0">
                <td colspan="6" class="text-center text-muted">Aucun article à valider.</td>
              </tr>
              </tbody>
              <tfoot>
              <tr>
                <th colspan="4" class="text-end">Sous-total :</th>
                <th colspan="2">{{ totalAmount }} FCFA</th>
              </tr>
              </tfoot>
            </table>

          </div>


          <!-- Lecture seule si VALIDATED ou REJECTED -->
          <div class="card-body" v-else>
            <div v-for="line in sale.saleLines" :key="line.id" class="d-flex mb-3">
              <div class="flex-grow-1">
                <p><strong>{{ line.productCode }}</strong></p>
                <p class="text-muted small">PU : {{ line.unitPrice }} FCFA</p>
                <p class="text-muted small">Qté validée : {{ line.validatedQuantity }}</p>
              </div>
              <div class="text-end">
                <strong>{{ (line.unitPrice * line.validatedQuantity).toLocaleString('fr-FR') }} FCFA</strong>
              </div>
            </div>
            <hr>
            <h4 class="text-end">Total : {{ sale.totalAmount.toLocaleString('fr-FR') }} FCFA</h4>
          </div>
        </div>

      </div>

      <!-- Colonne droite -->
      <div class="col-xl-4 col-md-12">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Méthode de paiement</h3>
          </div>
          <div class="card-body">
            <p><strong>Méthode :</strong> {{ sale.paymentMethod || 'Non précisé' }}</p>
            <p v-if="sale.paymentMethod === 'TRANSFER'">
              Virement à effectuer sur le compte XXXXX.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal historique -->
    <div class="modal fade" id="modal-history" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-scrollable modal-md">
        <div class="modal-content">
          <div class="modal-header bg-secondary text-white">
            <h5 class="modal-title">Historique de la vente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <ul class="list-group">
              <li v-for="h in history" :key="h.timestamp" class="list-group-item">
                <strong>{{ h.status }}</strong> –
                <small>{{ h.timestamp | dateFormat('dddd D MMMM YYYY HH:mm') }}</small>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import {confirmAction} from "../../../helpers/helpers";

  export default {
    name: "SaleDetails",
    data() {
      return {
        sale: {},
        editableLines: [],
        history: [],
      };
    },
    computed: {
      totalAmount() {
        return this.editableLines
            .reduce((sum, line) => sum + (line.unitPrice * line.validatedQuantity), 0);
      }
    }
,
    methods: {
      goBack() {
        this.$router.push({ name: 'adminSalesPage' });
      },
      increment(line) {
        line.validatedQuantity++;
      },
      decrement(line) {
        if (line.validatedQuantity > 1) {
          line.validatedQuantity--;
        }
      },
      removeLine(lineId) {
        this.editableLines = this.editableLines.filter(l => l.id !== lineId);
      },
      async validateSale() {
        const { confirmed } = await confirmAction('validate');
        if (!confirmed) return;
        const payload = {
          reference: this.sale.reference,
          updates: this.editableLines.map(line => ({
            saleLineId: line.id,
            validatedQuantity: line.validatedQuantity
          })),
          totalAmount: parseFloat(this.totalAmount)
        };



          this.$loading(true);
          this.$http.put(`/sales/${this.sale.reference}/validate`, payload)
              .then(() => {
                swal("Succès", "Vente validée avec les quantités ajustées.", "success");
                this.loadSale();
              })
              .catch(() => swal("Erreur", "Impossible de valider la vente.", "error"))
              .finally(() => this.$loading(false));
      },
      loadSale() {
        this.$loading(true);
        const id = this.$route.params.id;
        this.$http.get(`/sales/${id}`)
            .then(({ data }) => {
              this.sale = data;
              if (data.status === 'PENDING') {
                this.editableLines = data.saleLines.map(line => ({
                  id: line.id,
                  productCode: line.productCode,
                  unitPrice: line.unitPrice,
                  requestedQuantity: line.requestedQuantity ?? 1,
                  validatedQuantity: line.requestedQuantity ?? line.requestedQuantity ?? 1
                }));
              }
            })
            .catch(() => {
              this.editableLines = [];
              this.$loading(false);
            })
            .finally(() => this.$loading(false));
      },

      async rejectSale() {
        const { confirmed } = await confirmAction('reject');
        if (!confirmed) return;

            this.$loading(true);
            this.$http.put(`/sales/${this.sale.id}/reject`, {
              comment: result.value
            })
                .then(() => {
                  swal("Rejetée", "Vente rejetée avec succès.", "success");
                  this.loadSale();
                })
                .catch(err => {
                  console.error(err);
                  swal("Erreur", "Impossible de rejeter la vente.", "error");
                })
                .finally(() => this.$loading(false));
      },

      showHistory() {
        this.$http.get(`/sales/${this.sale.id}/history`)
            .then(({ data }) => {
              this.history = data;
              $('#modal-history').modal('show');
            });
      },
    },
    mounted() {
      this.loadSale();
    }
  };
</script>
